'use client';

import { Bunja, bunja } from 'bunja';
import { atom, type Atom } from 'jotai';
import Cookies from 'js-cookie';

import { JotaiStoreScope } from './bunja-jotai';
import { isServer } from './env-helper';

export type CookieKey = (typeof CookieKeys)[keyof typeof CookieKeys];

export const CookieKeys = {
  Nuts: 'session_id_nuts',
  ReferralPath: 'referral_path',
  ReferralCode: 'referral_code',
  ReferralType: 'referral_type',
  ReferrerSymbol: 'referrer_symbol',
  SpotReferrerSymbol: 'spot_referrer_symbol',
  TradFiReferrerSymbol: 'tradfi_referrer_symbol',
  Fbc: 'fbc',
  Webview: 'webview',
  AppVersion: 'appVersion',
  OsType: 'osType',
  OsVersion: 'osVersion',
  Internal: 'internal',
} as const;
export const consentRequiredCookies = [
  CookieKeys.ReferrerSymbol,
  CookieKeys.SpotReferrerSymbol,
  CookieKeys.TradFiReferrerSymbol,
];

export const getCookieNutsDomain = () => {
  if (isServer) return '';
  // prod.yaml:  cookie_domains: ['.aqx.com', '.prod.prex.run']
  // sandbox.yaml:  cookie_domains: ['.sandbox.prex.run']
  // qa.yaml:  cookie_domains: ['.qa.prex.run']
  // stg.yaml:  cookie_domains: ['.stg.prex.run']
  // local.yaml:  cookie_domains: ['.yoda.mobi']
  // configmap.yaml:  COOKIE_DOMAINS: {{ join "," .Values.app.cookie_domains }}
  // prod.yaml:  cookie_domains: ['.aqx.com', '.prod.prex.run']
  // sandbox.yaml:  cookie_domains: ['.sandbox.prex.run']
  // qa.yaml:  cookie_domains: ['.qa.prex.run']
  // stg.yaml:  cookie_domains: ['.stg.prex.run']
  // local.yaml:  cookie_domains: ['.yoda.mobi']
  // NOTE: prex.run은 prod도 포함하여 서버에서 보내주는 도메인을 통일 할 수가 없다.
  const { hostname } = window.location;
  if (hostname.endsWith('localhost')) return hostname;
  if (hostname.endsWith('.yoda.mobi')) return '.yoda.mobi'; // 'luke.yoda.mobi', api-luke.yoda.mobi for bolts, .yoda.mobi for nuts
  return `.${hostname}`;
};

export const getStrictDomain = () => {
  if (isServer) return '';
  return window.location.hostname;
};

type Listener = () => void;

const CookieStore = () => {
  const listeners = new Set<Listener>();
  const cookieOptions = { path: '/', domain: getCookieNutsDomain() };

  const sync = () => listeners.forEach((l) => l());

  const subscribe = (listener: Listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const get = (key: string) => Cookies.get(key);

  const update = (key: string, value: string, expires: number | Date | undefined) => {
    Cookies.set(key, value, { expires, ...cookieOptions });
    sync();
  };

  const remove = (key: string) => {
    Cookies.remove(key, cookieOptions);
    sync();
  };

  return {
    sync,
    subscribe,
    get,
    update,
    remove,
  };
};

export const cookieStore = CookieStore();

export interface CookieBunjaInstance {
  cookieAtom: Atom<string | undefined>;
  sync(): void;
  update(value: string, expires?: number | Date): void;
  remove(): void;
}

const cookieBunjas = {} as Record<CookieKey, Bunja<CookieBunjaInstance>>;

export function getCookieBunja(key: CookieKey): Bunja<CookieBunjaInstance> {
  if (key in cookieBunjas) return cookieBunjas[key];

  const cookieBunja = bunja(() => {
    const store = bunja.use(JotaiStoreScope);
    const cookieAtom = atom(cookieStore.get(key));
    // const cookieUpdateBlockedAtom = atom((get) => {
    //   const consentStatus = get(consentStatusAtom);
    //   return consentStatus !== 'all' && consentRequiredCookies.some((c) => c === key);
    // });
    cookieAtom.debugLabel = `cookie - ${key}`;

    bunja.effect(() => {
      store.set(cookieAtom, cookieStore.get(key));
      return cookieStore.subscribe(() => {
        store.set(cookieAtom, cookieStore.get(key));
      });
    });

    return {
      cookieAtom,
      sync: cookieStore.sync,

      update(value: string, expires?: number | Date) {
        // if (store.get(cookieUpdateBlockedAtom)) return;
        cookieStore.update(key, value, expires);
      },

      remove() {
        cookieStore.remove(key);
      },
    };
  });
  cookieBunja.debugLabel = `cookieBunja - ${key}`;

  cookieBunjas[key] = cookieBunja;
  return cookieBunja;
}
